import React, { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { RootStoreContext } from '../../global/storeContext';
import { TenantStore } from '../../stores/tenantStore';
import { UserStore } from '../../stores/userStore';
import { GridContent, GridMainContent, GridSideContent } from '../uiComponents/UIComponents';
import { WizardStep } from '../../interfaces/wizard';
import { Button, debounce, Grid, SwipeableDrawer, Typography } from '@material-ui/core';
import { WizardComponent } from '../uiComponents/wizard';
import { DatePicker } from '../datePicker/datepicker';
import { BioInfoComponent } from './bioInfo';
import { AddressInfoComponent } from './addressInfo';
import { FinanceInfoComponent } from './financeInfo';
import { AdditionalInfoComponent } from './additionalInfo';
import { IsValidNumber, StringIsNullOrWhitespace } from '../../global/util';
import { isValidAddress } from '../../utils/address';
import { useStyles } from './styles';
import { format } from 'date-fns';
import {
    ITenantProfile,
    IdPropertyListing,
    IRentApplicationForm,
    IRentApplication,
    RentApplicationStatus,
    IUserBio,
    IListingTerm,
    IOccupation,
} from 'realhaus-sdk';
import { BackgroundCheckConsentDialog } from './dialogs/backgroundCheckConsentDialog';
import { useIdentityGate } from '../../hooks/useIdentityGate';
import { CompleteAccountSetupDialog } from './dialogs/completeAccountSetupDialog';

export const fullTenantInfo = async (tenantStore: TenantStore, userStore: UserStore) => {
    const profile = await tenantStore.getTenantProfile().then((p) => {
        if (!p) {
            const defaultOccupation = { incomeType: 'monthly' } as IOccupation;
            const tp = {} as ITenantProfile;
            tp.occupation = [defaultOccupation];
            return tp;
        } else {
            return p;
        }
    });
    const userProfile = await userStore.getProfile();
    const bio: IUserBio | undefined = !!userProfile
        ? {
              firstname: userProfile?.firstname,
              lastname: userProfile?.lastname,
              dateOfBirth: userProfile?.dateOfBirth,
              gender: userProfile?.gender,
              phoneNumber: userProfile?.phoneNumber,
              photoURL: userProfile?.photoURL,
          }
        : undefined;

    const idVerified = userProfile?.isVerified ?? false;

    return { profile, bio, idVerified };
};

enum RentApplicationSteps {
    MOVEINFO,
    BIO,
    ADDRESS,
    FINANCE,
    ADDITIONALINFO,
}

export const RentApplicationComponent: React.FC<{
    listingId: string;
    onSubmitted: () => void;
    onCancel: () => void;
    leaseTerm: IListingTerm | undefined;
}> = observer(({ listingId, onSubmitted, onCancel, leaseTerm }) => {
    const { tenantStore, userStore, uiStore, listingStore } = useContext(RootStoreContext);
    const { checkAndProceed, gateDialogProps } = useIdentityGate();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [listing, setListing] = React.useState<IdPropertyListing | undefined>();
    const [userBio, setUserBio] = useState<IUserBio>({} as IUserBio);
    const [tenantProfile, setTenantProfile] = useState<ITenantProfile>({} as ITenantProfile);
    const [rentApplication, setRentApplication] = useState({} as IRentApplicationForm);
    const [userProfileVerified, setUserProfileVerified] = useState<boolean>(false);
    const [unsubscribe, setUnsubscribe] = React.useState<any>();

    const { userInfo } = userStore;

    const debounceSave = React.useMemo(() => {
        return debounce(async (profile: ITenantProfile) => {
            await tenantStore.updateProfile(profile);
        }, 2500);
    }, [listingId]);

    const handleProfileChange = (prop: keyof ITenantProfile) => async (value: any) => {
        const updated = { ...tenantProfile, [prop]: value };
        setTenantProfile(updated);
        debounceSave(updated);
    };

    const handleUpdateUserBio = (prop: keyof IUserBio) => (value: any) => {
        setUserBio({ ...userBio, [prop]: value });
    };

    const handleRentApplicationChange = (prop: keyof IRentApplicationForm) => (value: any) => {
        setRentApplication({ ...rentApplication, [prop]: value });
    };

    const handleSubmitApplication = () => {
        const invalidSteps = steps.filter((x) => !x.validate());
        if (invalidSteps.length > 0) {
            const errMsg = `Steps ${invalidSteps
                .map((s) => s.label)
                .join(
                    ',',
                )} have incomplete fields. Complete all fields then try submit your application again`;
            uiStore.error(errMsg);
            return;
        }

        checkAndProceed(() => {
            try {
                // all steps valid, submit application
                // save tenant profile
                tenantStore
                    .updateProfile(tenantProfile)
                    .then(() => {
                        tenantStore
                            .submitRentApplication(listingId, rentApplication)
                            .then((application) => {
                                onSubmitted();
                            })
                            .catch((err) => {
                                console.log('Error submitting application', err);
                            });
                    })
                    .catch((err) => {
                        console.log('Error updating profile', err);
                    });
            } catch (err) {
                console.log('Error submitting application', err);
            }
        });
    };

    const minDate =
        Date.now() > (leaseTerm?.dateAvailable ?? 0)
            ? new Date(Date.now())
            : new Date(leaseTerm?.dateAvailable ?? 0);

    const steps: WizardStep[] = [
        {
            label: 'Move-In',
            step: RentApplicationSteps.MOVEINFO,
            component: (
                <MoveInDateComponent
                    minDate={minDate}
                    selectedDate={rentApplication.moveinDate}
                    onMoveInChanged={handleRentApplicationChange('moveinDate')}
                />
            ),
            validate: () => !!rentApplication.moveinDate && rentApplication.moveinDate > 0,
        },
        {
            label: 'Your Info',
            step: RentApplicationSteps.BIO,
            component: (
                <BioInfoComponent
                    profile={tenantProfile}
                    userBio={userBio}
                    updateProfile={handleProfileChange}
                    updateUserBio={handleUpdateUserBio}
                    userProfileVerified={userProfileVerified}
                />
            ),
            validate: () => {
                return (
                    !StringIsNullOrWhitespace(userBio.firstname) &&
                    !StringIsNullOrWhitespace(userBio.lastname) &&
                    !StringIsNullOrWhitespace(userBio.gender) &&
                    IsValidNumber(userBio.dateOfBirth) &&
                    !!tenantProfile.emergencyContact &&
                    !StringIsNullOrWhitespace(tenantProfile.emergencyContact.name) &&
                    !StringIsNullOrWhitespace(tenantProfile.emergencyContact.email) &&
                    !StringIsNullOrWhitespace(tenantProfile.emergencyContact.phone) &&
                    userProfileVerified
                );
            },
        },
        {
            label: 'Address',
            step: RentApplicationSteps.ADDRESS,
            component: (
                <AddressInfoComponent profile={tenantProfile} updateProfile={handleProfileChange} />
            ),
            validate: () =>
                !!tenantProfile.address &&
                isValidAddress(tenantProfile.address.address) &&
                IsValidNumber(tenantProfile.address.moveinDate) &&
                tenantProfile.address.moveinDate > 0,
        },
        {
            label: 'Income',
            step: RentApplicationSteps.FINANCE,
            component: (
                <FinanceInfoComponent profile={tenantProfile} updateProfile={handleProfileChange} />
            ),
            validate: () =>
                !!tenantProfile.occupation &&
                tenantProfile.occupation.length > 0 &&
                tenantProfile.occupation.every((occupation) => !!occupation.status),
        },
        {
            label: 'Additional Info',
            step: RentApplicationSteps.ADDITIONALINFO,
            component: (
                <AdditionalInfoComponent
                    rentApplication={rentApplication}
                    updateInfo={handleRentApplicationChange}
                />
            ),
            validate: () =>
                !!rentApplication.hasPets &&
                !!rentApplication.hasVehicle &&
                !!rentApplication.smokes,
        },
    ];

    const applicationHeader = () => {
        return (
            <>
                {/* <Typography variant='h6'>Rent Application</Typography> */}
                <Typography variant='h6' display='inline'>
                    <b>Rental Application for: &nbsp;</b>
                </Typography>
                <Typography variant='h5' display='inline'>
                    {listing?.address.unit ? `${listing?.address.unit} - ` : null}
                    {`${listing?.address.streetAddress}, ${listing?.address.city}, ${listing?.address.postalCode}`}
                </Typography>
            </>
        );
    };

    const updateTenantInfo = () => {
        fullTenantInfo(tenantStore, userStore).then(({ profile, bio, idVerified }) => {
            setTenantProfile(profile);
            setUserBio(bio!);
            setUserProfileVerified(idVerified);
        });
    };

    //listen to user profile update
    const userDocListener = () => {
        if (!!unsubscribe) return;
        if (!!userInfo) {
            const subscribedId = userStore.subscribeToUserChanges(userInfo.uid, () => {
                updateTenantInfo();
            });
            setUnsubscribe(subscribedId);
        }
    };

    useEffect(() => {
        listingStore.getListing(listingId).then((l) => {
            setListing(l);
        });

        updateTenantInfo();
        userDocListener();
    }, [tenantStore, userStore]);

    return (
        <>
            <GridMainContent>
                <WizardComponent
                    steps={steps}
                    title={applicationHeader()}
                    onSubmit={handleSubmitApplication}
                    onCancel={onCancel}
                    finishLabel='Submit Application'
                />
            </GridMainContent>
            <CompleteAccountSetupDialog {...gateDialogProps} />
        </>
    );
});

const MoveInDateComponent: React.FC<{
    selectedDate: number;
    onMoveInChanged: (date: number) => void;
    minDate: Date;
}> = observer(({ selectedDate, onMoveInChanged, minDate }) => {
    const [selectedMoveinDate, setSelectedMoveinDate] = React.useState<Date>(
        selectedDate ? new Date(selectedDate) : minDate,
    );

    const handleMoveinDateChange = (date: Date | null) => {
        setSelectedMoveinDate(date ?? new Date(Date.now()));
        onMoveInChanged(date?.getTime() ?? 0);
    };

    useEffect(() => {
        onMoveInChanged(selectedMoveinDate.getTime());
    }, []);

    return (
        <>
            <GridContent>
                <GridMainContent>
                    <Typography variant='h5' gutterBottom>
                        When will you like to move in?
                    </Typography>
                </GridMainContent>
                <GridMainContent withSidebar={true}>
                    <Grid item xs={12}>
                        <Typography variant='body1' gutterBottom>
                            MOVE-IN Date
                        </Typography>
                        <div style={{ maxWidth: '300px' }}>
                            <DatePicker
                                id='movein-picker'
                                selectedDate={selectedMoveinDate}
                                onChange={handleMoveinDateChange}
                                minDate={minDate}
                            />
                        </div>
                    </Grid>
                </GridMainContent>
                <GridSideContent>
                    <Typography variant='body1' align='justify'>
                        Let the landlord know when you will like move in.
                    </Typography>
                    <br />
                    <Typography variant='body1' align='justify'>
                        This date is negotiable.
                        <br />
                        Should your application get accepted, you can adjust this date when you sign
                        your lease.
                    </Typography>
                </GridSideContent>
            </GridContent>
        </>
    );
});

export const ApplyToRentButton: React.FC<{
    listingId: string;
    onSubmitted: () => void;
    leaseTerm: IListingTerm | undefined;
}> = observer(({ listingId, onSubmitted, leaseTerm }) => {
    const classes = useStyles();
    const { tenantStore, userStore } = useContext(RootStoreContext);
    const [rentApplication, setRentApplication] = useState<IRentApplication | null>(null);
    const [backgroundCheckConsentDialog, setBackgroundCheckConsentDialog] =
        React.useState<boolean>(false);
    const { checkAndProceed, gateDialogProps } = useIdentityGate();

    useEffect(() => {
        if (!!listingId) {
            tenantStore.getRentApplication(listingId).then((app) => {
                const openApplication = app?.find(
                    (x) =>
                        x.status === RentApplicationStatus.PENDING,
                );
                if (!!openApplication) setRentApplication(openApplication);
            });
        }
    }, [listingId]);

    const [rentApplicationModalOpen, setRentApplicationModalOpen] = React.useState<boolean>(false);

    const handleRentApplicationModalOpen = async () => {
        const requiresConsent = await userStore.requiresConsent();

        if (requiresConsent) {
            setBackgroundCheckConsentDialog(true);
            return;
        }
        setRentApplicationModalOpen(true);
    };
    const handleRentApplicationModalClose = () => {
        setRentApplicationModalOpen(false);
    };

    const handleRentApplicationSubmitted = () => {
        handleRentApplicationModalClose();
        onSubmitted();
    };

    const handleCloseBackgroundCheckConsentDialog = (consentCaptured?: boolean) => {
        if (!!consentCaptured) {
            setBackgroundCheckConsentDialog(false);
            setRentApplicationModalOpen(true);
        } else {
            setBackgroundCheckConsentDialog(false);
        }
    };

    return (
        <>
            {!!rentApplication ? (
                <Typography gutterBottom>
                    You applied to this listing on {format(rentApplication.dateSubmitted, 'PP')}
                </Typography>
            ) : (
                <Button
                    variant='outlined'
                    color='primary'
                    onClick={() => checkAndProceed(handleRentApplicationModalOpen)}
                >
                    Apply Now
                </Button>
            )}

            {/* Rental Application Modal */}
            <SwipeableDrawer
                className={classes.applicationDrawer}
                anchor='right'
                open={rentApplicationModalOpen}
                onOpen={handleRentApplicationModalOpen}
                onClose={handleRentApplicationModalClose}
            >
                <RentApplicationComponent
                    listingId={listingId}
                    leaseTerm={leaseTerm}
                    onSubmitted={handleRentApplicationSubmitted}
                    onCancel={handleRentApplicationModalClose}
                />
            </SwipeableDrawer>

            <BackgroundCheckConsentDialog
                openBackgroundCheckConsentDialog={backgroundCheckConsentDialog}
                handleCloseBackgroundCheckConsentDialog={handleCloseBackgroundCheckConsentDialog}
            />

            <CompleteAccountSetupDialog {...gateDialogProps} />
        </>
    );
});
